import { Palette, RefreshCw, ShieldCheck, Users } from "lucide-react";
import type { ReactNode } from "react";
import { Link, useParams } from "wouter";
import { PageHeader } from "../../components/ui";
import { useCompany } from "../../lib/company";
import { themeLocked } from "../../lib/theme";
import { AccessSection } from "./AccessSection";
import { AppearanceSection } from "./AppearanceSection";
import { PeopleSection } from "./PeopleSection";
import { ServiceM8Section } from "./ServiceM8Section";

/*
 * Settings, in sections at /settings/<section>. Everyone gets Appearance (their own theme);
 * managers also get ServiceM8 (the connection, and what's synced), Access (what each ServiceM8
 * role can do) and People (who has signed in): those are hidden for everyone else, and the
 * server checks too. /settings is the first one they can see.
 *
 * Settings is used now and then, so it isn't with the tools in the sidebar (a link at its foot
 * comes here). Here, the sections are tabs under its heading, as wide as the section showing,
 * on every screen size; the tab names the section, its description under them.
 */

/** The sections, in order. `subtitle`: what it's for, under the tabs. */
const SETTINGS_SECTIONS: { id: string; name: string; icon: ReactNode; subtitle: string; page: () => ReactNode; managers: boolean }[] = [
    {
        id: "servicem8",
        name: "ServiceM8",
        icon: <RefreshCw className="size-4" />,
        subtitle: "The connection, and a copy of your ServiceM8 data kept up to date automatically.",
        page: ServiceM8Section,
        managers: true,
    },
    {
        id: "access",
        name: "Access",
        icon: <ShieldCheck className="size-4" />,
        subtitle: "What each ServiceM8 security role can do here.",
        page: AccessSection,
        managers: true,
    },
    { id: "people", name: "People", icon: <Users className="size-4" />, subtitle: "Everyone who has signed in.", page: PeopleSection, managers: true },
    {
        id: "appearance",
        name: "Appearance",
        icon: <Palette className="size-4" />,
        subtitle: `Saved to your account, so it applies on all your devices.${themeLocked ? "" : " Also in the menu under your name."}`,
        page: AppearanceSection,
        managers: false,
    },
];


export function Settings() {
    const company = useCompany();
    const { section: id } = useParams<{ section?: string }>();
    const sections = SETTINGS_SECTIONS.filter((s) => company.canManage || !s.managers);
    const section = sections.find((s) => s.id === id) ?? sections[0];
    if (!section) return null;
    const Page = section.page;
    return (
        <div className="page">
            <PageHeader title="Settings" />
            {/* Part of the page: plain tabs, a line under them the page's width; the tab names the section. */}
            <nav className="tabs -mt-2 bg-transparent px-0" aria-label="Settings">
                {sections.map((s) => (
                    <Link key={s.id} href={`/settings/${s.id}`} aria-current={s === section ? "page" : undefined} className="tab">
                        {s.icon}
                        {s.name}
                    </Link>
                ))}
            </nav>
            <p className="mt-3 mb-6 text-sm text-muted">{section.subtitle}</p>
            <Page />
        </div>
    );
}
